import React, { useReducer, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loader from 'cccisd-loader';
import { Link } from 'cccisd-react-router';
import {
    loadAssessmentPlanList,
    loadStudentsPlansList,
    loadContactsAndSessions,
    loadProgress,
    setSelectedPlan,
} from 'js/reducers/assessments';
import AssessmentSummary from './AssessmentSummary';
import AssessmentTable from './AssessmentTable';
import UpcomingSessionsTable from './UpcomingSessionsTable';
import style from './style.css';
import _union from 'lodash/union';
import _find from 'lodash/find';

const Administer = props => {
    const [loading, setLoading] = useState(true);
    const [forceReloadQueries, setForceReloadQueries] = useReducer(s => s + 1, 0);

    const dispatch = useDispatch();
    const assessmentPlanList = useSelector(state => state.app.assessment.assessmentPlanList);
    const selectedPlan = useSelector(state => state.app.assessment.selectedPlan);
    const studentsPlansList = useSelector(state => state.app.assessment.studentsPlansList);
    const contacts = useSelector(state => state.app.assessment.contacts);

    useEffect(() => {
        (async () => {
            setLoading(true);
            await dispatch(loadAssessmentPlanList()).then(res => {
                if (
                    !selectedPlan.assignmentPlanId ||
                    !_find(res, { assignmentPlanId: selectedPlan.assignmentPlanId })
                ) {
                    dispatch(setSelectedPlan(res[0]));
                } else {
                    dispatch(
                        setSelectedPlan(res.find(plan => plan.assignmentPlanId === selectedPlan.assignmentPlanId))
                    );
                }
            });
            await dispatch(loadStudentsPlansList());
        })();
    }, [forceReloadQueries]);

    useEffect(() => {
        if (!selectedPlan && assessmentPlanList.length === 0) {
            setLoading(false);
        }
        if (selectedPlan?.assignmentPlanId && studentsPlansList.length > 0) {
            (async () => {
                setLoading(true);
                await dispatch(
                    loadContactsAndSessions({
                        assignmentPlanId: selectedPlan.assignmentPlanId,
                        pawnIds: studentsPlansList
                            .filter(learner =>
                                learner.selectedAssignmentPlanList.some(
                                    plan => plan.assignmentPlanId === selectedPlan.assignmentPlanId
                                )
                            )
                            .map(learner => learner.pawn.pawnId),
                    })
                ).then(async contactsAndSessions => {
                    await dispatch(
                        loadProgress({
                            sessions: contactsAndSessions.sessions,
                            contacts: contactsAndSessions.contacts,
                            pawnIds: _union(
                                studentsPlansList.map(spl => spl.pawn.pawnId),
                                contacts.map(c => c.pawn.pawnId)
                            ),
                            progressQuery: props.progressQuery,
                        })
                    );
                    setLoading(false);
                });
            })();
        }
        if (selectedPlan?.assignmentPlanId && studentsPlansList.length === 0) {
            setLoading(false);
        }
    }, [selectedPlan, studentsPlansList, forceReloadQueries]);

    if (loading) {
        return <Loader loading />;
    }

    if (!selectedPlan && assessmentPlanList.length === 0) {
        return (
            <div className={style.administerPage}>
                <div className={style.warning}>
                    No Assessment Plans found. Please create an Assessment Plan on the{' '}
                    <Link to="/students">Students Dashboard</Link> to get started.
                </div>
            </div>
        );
    }

    return (
        <div className={style.administerPage}>
            <div className={style.administerHeader}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <label htmlFor="assessmentPlan">Assessments Plans</label>
                    <select
                        name="assessmentPlan"
                        id="assessmentPlan"
                        value={selectedPlan.assignmentPlanId || ''}
                        onChange={e => {
                            return dispatch(
                                setSelectedPlan(
                                    assessmentPlanList.find(ap => ap.assignmentPlanId === Number(e.target.value))
                                )
                            );
                        }}
                    >
                        {assessmentPlanList.map(ap => (
                            <option key={ap.assignmentPlanId} value={ap.assignmentPlanId}>
                                {ap.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <header className={style.summaryHeading}>
                        <h3>{selectedPlan.label}</h3>
                        <p>{selectedPlan.description}</p>
                    </header>
                </div>
            </div>
            <AssessmentSummary
                key={'summary' + selectedPlan.assignmentPlanId}
                assessment={selectedPlan}
                setForceReloadQueries={setForceReloadQueries}
            />

            <AssessmentTable
                key={'table' + selectedPlan.assignmentPlanId}
                assessment={selectedPlan}
                setForceReloadQueries={setForceReloadQueries}
            />

            <UpcomingSessionsTable key={'upcoming' + selectedPlan.assignmentPlanId} assessment={selectedPlan} />
        </div>
    );
};

export default Administer;
